import { courseModuleApi } from '@/api/course-module.api';
import {
  CourseModuleResponseDTO,
  Prerequisite
} from '@/jbi-shared/dto/jtms.dtos';
import { RootState } from '@/store/store';
import { convertCourseModuleResponseDTO } from '@/utils/jtms-utils';
import { DispatchOptions } from 'vuex';
import {
  CourseModuleComponentItem,
  CourseModuleState,
  CreateCourseModulesApiDTO,
  DeleteCourseModulesApiDTO,
  IndexBooleanPayload,
  UpdateCourseModuleApiDTO,
  UpdateCourseModulePayload,
  UpdateCourseModulePrerequisitePayload
} from './course-module.types';

interface CourseModuleActionContext {
  commit: (mutation: string, payload?: any) => void;
  dispatch: (
    action: string,
    payload?: any,
    options?: DispatchOptions
  ) => Promise<any>;
  state: CourseModuleState;
  rootState: RootState;
}

export const courseModuleActions = {
  async createCourseModuleApi(
    { commit, dispatch, state }: CourseModuleActionContext,
    payload: CreateCourseModulesApiDTO
  ) {
    try {
      commit('CREATE_COURSE_MODULE_LOADING');

      const { data } = await courseModuleApi.createCourseModulesApi(payload);

      if (data) {
        commit('CREATE_COURSE_MODULE_SUCCESS');

        const { tempId, createdCourseModule } = data;

        // Convert API response into course package component for frontend
        const itemToUpdateIndex = state.courseModulesTabState.activeCourseModules.findIndex(
          (cm) => cm.tempId === tempId
        );

        if (itemToUpdateIndex > -1) {
          // Dropdown should be disabled only if there is only 1 course module
          const shouldDisable =
            state.courseModulesTabState.activeCourseModules.length === 1;

          const convertedCourseComponentItem: CourseModuleComponentItem = convertCourseModuleResponseDTO(
            [createdCourseModule],
            shouldDisable
          )[0];

          const updatePayload: UpdateCourseModulePayload = {
            index: itemToUpdateIndex,
            data: convertedCourseComponentItem
          };

          dispatch('updateCourseModule', updatePayload);
        }
      }
    } catch (error) {
      commit('CREATE_COURSE_MODULE_ERROR', error);
      throw error;
    }
  },

  async updateCourseModuleApi(
    { commit, dispatch, state }: CourseModuleActionContext,
    payload: UpdateCourseModuleApiDTO
  ) {
    try {
      commit('UPDATE_COURSE_MODULE_LOADING');

      const { data } = await courseModuleApi.updateCourseModulesApi(payload);

      if (data) {
        commit('UPDATE_COURSE_MODULE_SUCCESS');
        const updatedCourseModule = data[0];

        // Convert API response into course package component for frontend
        const itemToUpdateIndex = state.courseModulesTabState.activeCourseModules.findIndex(
          (cm) => cm.id === updatedCourseModule.id
        );

        if (itemToUpdateIndex > -1) {
          // Dropdown should be disabled only if there is only 1 course module
          const shouldDisable =
            state.courseModulesTabState.activeCourseModules.length === 1;
          const itemToUpdate =
            state.courseModulesTabState.activeCourseModules[itemToUpdateIndex];

          const convertedCourseComponentItem: CourseModuleComponentItem = convertCourseModuleResponseDTO(
            [updatedCourseModule],
            shouldDisable,
            itemToUpdate.requiredByIds
          )[0];

          const updatePayload: UpdateCourseModulePayload = {
            index: itemToUpdateIndex,
            data: convertedCourseComponentItem
          };

          dispatch('updateCourseModule', updatePayload);
        }
      }
    } catch (error) {
      commit('UPDATE_COURSE_MODULE_ERROR', error);
      throw error;
    }
  },

  async deleteCourseModuleApi(
    { commit, dispatch, state }: CourseModuleActionContext,
    payload: DeleteCourseModulesApiDTO
  ) {
    try {
      commit('DELETE_COURSE_MODULE_LOADING');

      const { data } = await courseModuleApi.deleteCourseModulesApi(payload);

      if (data) {
        commit('DELETE_COURSE_MODULE_SUCCESS');
        // Search for deleted item in active array
        const itemToRemoveIndex = state.courseModulesTabState.activeCourseModules.findIndex(
          (cm) => cm.id === data[0].id
        );

        if (itemToRemoveIndex > -1) {
          dispatch('removeActiveCourseModule', itemToRemoveIndex);
        }
      }
    } catch (error) {
      commit('DELETE_COURSE_MODULE_ERROR', error);
      throw error;
    }
  },

  /**
   * Clears the list of active course modules. Revalidates all fields.
   */
  clearActiveCourseModules({ commit, dispatch }: CourseModuleActionContext) {
    commit('CLEAR_ACTIVE_COURSES_MODULES');
    dispatch('validateAllCourseModules');
  },

  /**
   * Clears the list of deleted course modules.
   */
  clearDeletedCourseModules({ commit }: CourseModuleActionContext) {
    commit('CLEAR_DELETED_COURSES_MODULES');
  },

  /**
   * Adds a new course module to the active list. Automatically updates the label
   * and revalidates all fields.
   * @param payload - The course module item to add.
   */
  addActiveCourseModule(
    { commit, dispatch }: CourseModuleActionContext,
    payload: CourseModuleComponentItem
  ) {
    commit('ADD_ACTIVE_COURSE_MODULES', payload);
    dispatch('updateCourseModuleLabel');
    dispatch('coursePackage/validateCourseModuleDropdownState', undefined, {
      root: true
    });
    dispatch('recalculateState');
  },

  /**
   * Removes a course module from the active list by its index.
   * Additionally, removes all references to the deleted module from the
   * `prerequisiteIds` and `requiredByIds` arrays of all other active modules.
   * @param index - The index of the course module to remove.
   */
  removeActiveCourseModule(
    { commit, dispatch, state, rootState }: CourseModuleActionContext,
    index: number
  ) {
    // Also remove references from Course Packages, pass in ID to delete
    const courseModuleToDeleteId =
      state.courseModulesTabState.activeCourseModules[index].id;

    // The sequence of actions here is IMPORTANT, DO NOT REORDER
    // 1. Prior to removing course modules from state, first remove references from course packages
    dispatch(
      'coursePackage/removeCourseModuleReferences',
      courseModuleToDeleteId,
      { root: true }
    );

    // 2. Only then, remove course module from course module state
    commit('REMOVE_ACTIVE_COURSE_MODULES', index);

    // 3. Update all labels for remaining items
    dispatch('updateCourseModuleLabel');

    // 4. Recalculate course package state, which includes checking validation/duplication errors
    dispatch('coursePackage/recalculateState', undefined, { root: true });

    // 5. Finally, calculate course module state
    dispatch('recalculateState');
  },

  /**
   * Adds a course module to the deleted list (for tracking deletions).
   * @param payload - The course module item to mark as deleted.
   */
  addDeletedCourseModule(
    { commit }: CourseModuleActionContext,
    payload: CourseModuleComponentItem
  ) {
    commit('ADD_DELETED_COURSE_MODULES', payload);
  },

  /**
   * Assigns a list of existing course modules, converting them to the component's state format.
   * This action clears the existing active list before adding the new ones.
   * @param payload - An array of course modules from an API response.
   */
  assignExistingCourseModules(
    { commit, dispatch }: CourseModuleActionContext,
    payload: CourseModuleResponseDTO[]
  ) {
    commit('ASSIGN_EXISTING_COURSE_MODULES', payload);
    dispatch('recalculateState');
  },

  /**
   * Updates a specific course module in the active list by its index with new data.
   * @param payload - An object containing the index and the partial data to update.
   */
  updateCourseModule(
    { commit, dispatch }: CourseModuleActionContext,
    payload: UpdateCourseModulePayload
  ) {
    commit('UPDATE_COURSE_MODULE', payload);
    dispatch('updateCourseModuleLabel');
    dispatch('recalculateState');
  },

  /**
   * Updates the prerequisites for a specific course module in the active list by its index.
   * Assigns `requiredBy` and `prerequisites` arrays.
   * @param payload - An object containing the index and the partial data to update.
   */
  updateCourseModulePrerequisites(
    { commit, dispatch }: CourseModuleActionContext,
    payload: UpdateCourseModulePayload
  ) {
    dispatch('updateCourseModule', payload);

    const updatePrerequisitePayload: UpdateCourseModulePrerequisitePayload = {
      index: payload.index,
      data: payload.data.prerequisiteIds as Prerequisite[]
    };
    commit('UPDATE_COURSE_MODULE_PREREQUISITES', updatePrerequisitePayload);

    // Re-calculate the disabled states across all modules based on the changes
    dispatch('validatePrerequisiteDropdownState');
  },

  /**
   * Course module labels are dynamically assigned upon creation, based on index.
   * This action ensures correct labels are reassigned when course modules are
   * deleted/added.
   */
  updateCourseModuleLabel({ commit }: CourseModuleActionContext) {
    commit('UPDATE_COURSE_MODULE_LABEL');
  },

  /**
   * Updates the validation error status for a single course module.
   * @param payload - An object containing the index and the boolean error status.
   */
  updateCourseModuleFieldError(
    { commit, dispatch }: CourseModuleActionContext,
    payload: IndexBooleanPayload
  ) {
    commit('UPDATE_COURSE_MODULE_FIELD_ERROR', payload);
    dispatch('validateAllCourseModules');
  },

  /**
   * Updates the prerequisite dropdown state for a single course module, either enables/disables it
   * @param payload - An object containing the index and the boolean error status.
   */
  updateCourseModulePrerequisiteDropdownState(
    { commit }: CourseModuleActionContext,
    payload: IndexBooleanPayload
  ) {
    commit('UPDATE_COURSE_MODULE_PREREQUISITE_DROPDOWN_STATE', payload);
  },

  /**
   * Manual trigger to override the validation status for the entire course module form.
   * @param hasError - A boolean indicating if the form has errors.
   */
  updateCourseModuleFormError(
    { commit }: CourseModuleActionContext,
    hasError: boolean
  ) {
    commit('UPDATE_COURSE_MODULE_FORM_ERROR', hasError);
  },

  /**
   * Triggers a validation check across all active course modules to determine
   * the overall form error state.
   */
  validateAllCourseModules({ commit, rootState }: CourseModuleActionContext) {
    const hasCourseModules: boolean =
      rootState.course.courseCreationState.hasModules;
    commit('VALIDATE_ALL_COURSE_MODULES', hasCourseModules);
  },

  /**
   * Triggers a validation of all prerequisite dropdown states. This is
   * called after any prerequisite relationships have been changed.
   */
  validatePrerequisiteDropdownState({ commit }: CourseModuleActionContext) {
    commit('VALIDATE_PREREQUISITE_DROPDOWN_STATE');
  },

  /**
   * Manual trigger to re-calculate the states across all modules based on the changes.
   */
  recalculateState({ dispatch }: CourseModuleActionContext) {
    dispatch('validateAllCourseModules');
    dispatch('validatePrerequisiteDropdownState');
  }
};
